import { useState, useEffect, useRef } from "react";

export function useModal(setIsSubmitted: () => void, closeDelay = 300) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalAnimation, setModalAnimation] = useState("");
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);

  const openModal = () => {
    setIsOpen(true);
    setModalAnimation("modalFadeIn");
  };

  const closeModal = () => {
    setModalAnimation("modalFadeOut");
    setTimeout(() => {
      setIsOpen(false);
      setModalAnimation("");
      setIsSubmitted();
    }, closeDelay);
  };

  // Manage focus on open/close.
  useEffect(() => {
    if (isOpen) {
      modalRef.current?.querySelector<HTMLElement>(".close-button")?.focus();
    } else {
      triggerButtonRef.current?.focus();
    }
  }, [isOpen]);

  // Close modal on ESC or clicking outside.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) closeModal();
    };
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        closeModal();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return { isOpen, modalAnimation, openModal, closeModal, modalRef, triggerButtonRef };
}
